import { Component, type ErrorInfo, type ReactNode } from "react";
import { withTranslation, type WithTranslation } from "react-i18next";
import { Button, Card } from "@axisnode/ui";
import { emitObs } from "../obs";

type Props = WithTranslation & {
  screen: "ProjectList" | "ProjectDetail" | "DocumentDetail";
  children: ReactNode;
};

type State = { error: Error | null };

/**
 * Per-screen React Error Boundary (OE §1.17 / OA-02).
 * Distinct from §11 backend ErrorState. Class component — useTranslation()
 * (a hook) isn't usable here, so wrapped with withTranslation() instead.
 */
class ScreenErrorBoundaryBase extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    emitObs("error", "render_boundary", {
      screen: this.props.screen,
      message: error.message,
      componentStack: info.componentStack ?? "",
    });
  }

  render() {
    const { t } = this.props;
    if (this.state.error) {
      return (
        <div data-testid={`error-boundary-${this.props.screen}`} role="alert">
          <Card title={t("errorBoundary.title")}>
            <p>{t("errorBoundary.body", { screen: this.props.screen })}</p>
            <pre style={{ fontSize: "var(--ax-type-xs)", whiteSpace: "pre-wrap" }}>
              {this.state.error.message}
            </pre>
            <Button
              variant="secondary"
              onClick={() => this.setState({ error: null })}
            >
              {t("errorBoundary.retryScreen")}
            </Button>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

export const ScreenErrorBoundary = withTranslation()(ScreenErrorBoundaryBase);
